import React, { useMemo } from "react";

import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useDevice, useNavigate, useQueryHook, useUrlHooks } from "hooks";
import { useAuthActions } from "store";

import { ReactComponent as AltiusGlobalInc } from "./altius-global-inc.svg";
import { ReactComponent as AltiusLogo } from "./altius-logo.svg";
import { ReactComponent as AltiusTitle } from "./altius-title.svg";
import { SignUpForm } from "./SignUp-from";

export const SignUp: React.FC = () => {
  const { checkInviteTokenAction } = useAuthActions();
  const { queryParams } = useUrlHooks();
  const { isMobile } = useDevice();
  const navigate = useNavigate();

  const { d } = queryParams;
  const { error, loading, data: userData } = useQueryHook(async () => checkInviteTokenAction(d));

  const renderContent = useMemo(() => {
    if (loading) {
      return (
        <Stack sx={{ justifyContent: "center", alignItems: "center", height: "100%" }}>
          <CircularProgress size={200} />
        </Stack>
      );
    } else if (!d) {
      return (
        <Typography sx={{ fontSize: "1.5rem", fontWeight: 300, color: "#173c4c" }}>
          Signing up to an account can be done by invite only.
        </Typography>
      );
    } else if (d && !error) {
      return <SignUpForm inviteToken={d} userData={userData} />;
    } else if (error?.status === 403) {
      navigate({ route: "expired" });
      return <></>;
    } else {
      return (
        <Typography data-testid="expired-invite-message" sx={{ fontSize: "1.5rem", fontWeight: 300, color: "#173c4c" }}>
          This invite seems to be invalid, please contact the person who invited you into the account.
        </Typography>
      );
    }
  }, [error, d, loading, userData]);

  return (
    <Stack
      data-testid="sign-up-page"
      sx={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        ...(isMobile
          ? { flexDirection: "column", bgcolor: "#FFFFFF", p: "20px" }
          : { flexDirection: "row", bgcolor: "#f5f7f7" })
      }}
    >
      {!isMobile && (
        <Box
          sx={{
            bgcolor: "#173c4c",
            minWidth: "30%",
            maxWidth: "30%"
          }}
        />
      )}

      {isMobile ? (
        <>
          <Stack sx={{ flexDirection: "row", alignItems: "center", gap: "5px", mb: "60px" }}>
            <AltiusLogo />
            <Stack sx={{ gap: "5px" }}>
              <AltiusTitle />
              <AltiusGlobalInc />
            </Stack>
          </Stack>
          <Typography
            sx={{
              color: "#0A3142",
              fontFamily: "Merriweather",
              fontSize: "40px",
              fontWeight: 400,
              lineHeight: "117.5%",
              textTransform: "capitalize"
            }}
          >
            Sign up
          </Typography>
          <Typography
            sx={{
              color: "#0A3142",
              fontFamily: "Lato",
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: "normal",
              mb: "42px"
            }}
          >
            To add a deal you need to register in Altius
          </Typography>
          <Stack sx={{ overflow: "auto", p: "5px 0", flexGrow: 1 }}>{renderContent}</Stack>
        </>
      ) : (
        <Stack
          sx={{
            flexGrow: 1,
            p: "30px 25px 5px 110px"
          }}
        >
          <Typography
            sx={{
              fontFamily: "Oswald, sans-serif",
              fontSize: "2.5rem",
              fontWeight: 300,
              letterSpacing: "1.25px",
              lineHeight: "60px",
              mb: "50px",
              color: "#173c4c"
            }}
          >
            Sign Up
          </Typography>
          {renderContent}
        </Stack>
      )}
    </Stack>
  );
};
